import * as React from 'react';

import { PRODUCT_TYPE_NAMES } from 'bundles/browse/constants';
import PrivateSessionEditDrawerContent from 'bundles/enterprise-admin-drawers/components/PrivateSessionEditDrawerContent';
import type { CourseSession, SessionOptionType } from 'bundles/enterprise-admin-drawers/components/types';
import usePrivateSessionEditDrawerData from 'bundles/enterprise-admin-drawers/components/usePrivateSessionEditDrawerData';
import useSessionInfo from 'bundles/enterprise-admin-drawers/components/useSessionInfo';

export type Props = {
  isOpen: boolean;
  productType?: string;
  productId?: string;
  programId?: string;
  // Set the initial selected session and credit status
  override?: CourseSession & {
    isSelectedForCredit?: boolean;
    isAutoEnrollmentEnabled?: boolean;
    isSelectedToRequireAllItemCompletion?: boolean;
  };
  isIncludedInContentCuration?: boolean;
  onRequestClose: () => void;
  onSavedSessionMetadata?: () => void;
  onSelectSession?: ({
    // if onSelectSession is passed we return the selected data instead of persisting it
    productId,
    selectedSession,
    isSelectedForCredit,
    isSelectedToRequireAllItemCompletion,
    isAutoEnrollmentEnabled,
    productType,
  }: {
    productId: string;
    selectedSession: CourseSession;
    selectedSessionOption: SessionOptionType;
    isSelectedForCredit: boolean;
    isSelectedToRequireAllItemCompletion: boolean;
    isAutoEnrollmentEnabled: boolean;
    productType?: string;
  }) => void;
  useAsDialogContent?: boolean;
  onLoadingStateChange?: (x0: boolean) => void;
  submitting?: boolean;
  onSubmittingStateChange?: (x0: boolean) => void;
  onHandleDialogRedirect?: () => void;
  handleDialogFlowComplete?: boolean;
  onHasErrorStateChange?: (x0: boolean) => void;
  currentSessionOption?: SessionOptionType;
  onCurrentSessionOptionChange?: (x0: SessionOptionType | undefined) => void;
};

const PrivateSessionEditDrawer: React.FC<Props> = (props) => {
  const { productType, productId, programId, useAsDialogContent } = props;

  const isS12n = productType === PRODUCT_TYPE_NAMES.SPECIALIZATION;

  const { lenOfS12n, course, s12n, isProfessionalCertificate, metaData, isDrawerDataLoading } =
    usePrivateSessionEditDrawerData({ isS12n, productId, programId, useAsDialogContent });

  const {
    productConfig,
    sessions = [],
    isSessionInfoLoading,
    naptime,
  } = useSessionInfo({ metadata: metaData, isS12n, productId, programId, shouldWaitToQuery: isDrawerDataLoading });

  const isDataLoading = isDrawerDataLoading || isSessionInfoLoading;

  if (isDataLoading || !productId || !programId) return null;

  return (
    <PrivateSessionEditDrawerContent
      {...props}
      naptime={naptime}
      lenOfS12n={lenOfS12n}
      s12n={s12n}
      course={course}
      isProfessionalCertificate={isProfessionalCertificate}
      metaData={metaData}
      productConfig={productConfig}
      sessions={sessions}
    />
  );
};

export default PrivateSessionEditDrawer;
